/**
 * O RealtimeKit já mostra a tela compartilhada no palco e tem tela cheia.
 * Quem compartilha, porém, só recebe uma prévia de 50% (no máximo 384px);
 * "Expandir prévia" vai a 60%. No celular essa prévia some.
 * O botão de tela cheia de quem assiste só aparece no mouse, então no toque
 * não dá para achar.
 *
 * O estilo mora no shadow root do kit. CSS da página não entra lá.
 */

const STYLE_ATTR = "data-constela-screenshare-stage";
const FULLSCREEN_ATTR = "data-constela-screenshare-fullscreen";

export const SCREENSHARE_STAGE_CSS = `
:host(.isSelf) #video-container,
:host(.isSelf) #video-container.expand,
:host([size='sm'].isSelf) #video-container,
:host([size='md'].isSelf) #video-container {
  display: block;
  position: absolute;
  inset: 0;
  width: 100%;
  max-width: none;
  height: 100%;
}
:host(.isSelf) #self-message {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  z-index: 3;
  margin: 0;
}
:host(.isSelf) h3 {
  margin: 0 0 8px;
  font-size: 14px;
}
#controls {
  display: flex;
  z-index: 4;
}
[${FULLSCREEN_ATTR}] {
  border: 0;
  border-radius: 8px;
  background: rgba(44, 44, 44, 0.92);
  color: #fff;
  padding: 8px 12px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
`;

function walkTree(root: ParentNode, onElement: (el: HTMLElement) => void): void {
  const walk = (node: ParentNode) => {
    const elements =
      node instanceof Element
        ? [node, ...Array.from(node.querySelectorAll("*"))]
        : Array.from(node.querySelectorAll("*"));
    for (const el of elements) {
      if (el instanceof HTMLElement) onElement(el);
      if (el instanceof HTMLElement && el.shadowRoot) walk(el.shadowRoot);
    }
  };
  walk(root);
}

function enterScreenshareFullscreen(host: HTMLElement, shadow: ShadowRoot): void {
  const video = shadow.querySelector("video") as
    | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
    | null;
  const ios = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (ios && video?.webkitEnterFullscreen) {
    video.webkitEnterFullscreen();
    return;
  }
  if (document.fullscreenElement) {
    void document.exitFullscreen();
    return;
  }
  const request = host.requestFullscreen?.bind(host);
  if (request) void request();
}

function ensureSelfFullscreenButton(host: HTMLElement, shadow: ShadowRoot): void {
  if (!host.classList.contains("isSelf")) return;
  const actions = shadow.querySelector(".actions");
  if (!actions || actions.querySelector(`[${FULLSCREEN_ATTR}]`)) return;
  const button = document.createElement("button");
  button.type = "button";
  button.setAttribute(FULLSCREEN_ATTR, "");
  button.textContent = "Tela cheia";
  button.addEventListener("click", () => enterScreenshareFullscreen(host, shadow));
  actions.appendChild(button);
}

export function applyScreenshareStage(root: ParentNode | null): number {
  if (!root) return 0;
  let styled = 0;
  walkTree(root, (el) => {
    if (el.tagName.toLowerCase() !== "rtk-screenshare-view") return;
    const shadow = el.shadowRoot;
    if (!shadow) return;
    if (!shadow.querySelector(`[${STYLE_ATTR}]`)) {
      const style = document.createElement("style");
      style.setAttribute(STYLE_ATTR, "");
      style.textContent = SCREENSHARE_STAGE_CSS;
      shadow.appendChild(style);
    }
    ensureSelfFullscreenButton(el, shadow);
    styled += 1;
  });
  return styled;
}

export function enableScreenshareStage(root: ParentNode | null): () => void {
  if (!root) return () => undefined;
  const paint = () => applyScreenshareStage(root);
  paint();
  const observer = new MutationObserver(paint);
  observer.observe(root, { childList: true, subtree: true });
  const timer = window.setInterval(paint, 1000);
  return () => {
    observer.disconnect();
    window.clearInterval(timer);
  };
}
